import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useCalculator } from '../state/CalculatorProvider';

/**
 * "Count-up numbers", odometer-style (Brad, 2026-09-29). Replaces the parked
 * Sep 28 tick-the-text counter, which jittered because the number's width
 * changed every frame and re-centered it.
 *
 * Each digit sits in its own fixed-width slot (tabular figures) holding a
 * 0-9 reel that rolls vertically, so nothing moves sideways while it rolls.
 * Slots are keyed by their place counted from the right (ones, tens, ...),
 * so a digit keeps its slot across changes. A place the new value gains
 * (9,999 -> 10,000) appears at full width and fades in with its reel rolling
 * up from 0; a place it loses is dropped at once. No width animation: Brad
 * (2026-09-29) didn't want the number expanding outward as it grows.
 *
 * Timing: first appearance rolls up from 0 with an ease-in-out. The default
 * 2s follows the legacy count-ups (HW Index 2s swing, Plan Comparison
 * CountUp.js 2.5s); callers can shorten it with `firstMs` (HSA Savings uses
 * 1s — 2s felt far too slow there). Later changes (a stepper click) take at
 * most 0.9s with an ease-out settle so repeated clicks don't feel laggy.
 *
 * Off (card/placement setting) or prefers-reduced-motion: exactly today's
 * plain text, no wrapper. On: the rolling digits are aria-hidden and screen
 * readers get the final formatted value only.
 */

const DEFAULT_FIRST_MS = 2000;
const CHANGE_MS = 900;
const FIRST_EASE = 'cubic-bezier(0.45, 0, 0.55, 1)';
const CHANGE_EASE = 'cubic-bezier(0.25, 1, 0.5, 1)';
const REEL = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export interface OdometerSlot {
  /** Place from the right plus kind, e.g. "0d" = ones digit, "3s" = thousands separator. */
  key: string;
  char: string;
  digit: boolean;
  /** in = a place the number gained, fading in; on = was already there. */
  status: 'on' | 'in';
}

/** "$12,345" -> prefix "$", core "12,345", suffix "". The core is what rolls. */
export function splitNumber(text: string): { prefix: string; core: string; suffix: string } {
  const first = text.search(/\d/);
  if (first < 0) return { prefix: text, core: '', suffix: '' };
  let last = text.length - 1;
  while (!/\d/.test(text[last])) last--;
  return { prefix: text.slice(0, first), core: text.slice(first, last + 1), suffix: text.slice(last + 1) };
}

export function coreSlots(core: string): OdometerSlot[] {
  const n = core.length;
  return [...core].map((char, i) => {
    const digit = /\d/.test(char);
    return { key: `${n - 1 - i}${digit ? 'd' : 's'}`, char, digit, status: 'on' };
  });
}

/**
 * The row to render at the start of a change, before anything rolls: the new
 * value's slots, where kept slots hold their current digit (their reel rolls
 * from it) and new slots start with their reel at 0. Places the new value
 * doesn't have are simply gone.
 */
export function mergeSlots(current: OdometerSlot[], target: OdometerSlot[]): OdometerSlot[] {
  const byKey = new Map(current.map((s) => [s.key, s]));
  return target.map((t): OdometerSlot => {
    const cur = byKey.get(t.key);
    return cur ?? { ...t, char: t.digit ? '0' : t.char, status: 'in' };
  });
}

/** Second half of a change: point every kept/new reel at its target digit. */
export function applyTarget(slots: OdometerSlot[], target: OdometerSlot[]): OdometerSlot[] {
  const chars = new Map(target.map((t) => [t.key, t.char]));
  return slots.map((s) => (chars.has(s.key) ? { ...s, char: chars.get(s.key)! } : s));
}

/** A formatted result number that rolls to new values when Count-up numbers is on. */
export function Odometer({ value, format, firstMs = DEFAULT_FIRST_MS }: OdometerProps) {
  const { controlStyle } = useCalculator();
  if (!controlStyle.countUp || prefersReducedMotion()) return <>{format(value)}</>;
  return <OdometerRoll value={value} format={format} firstMs={firstMs} />;
}

interface OdometerProps {
  value: number;
  format: (n: number) => string;
  /** Length of the first roll up from 0. Later changes take min(this, 0.9s). */
  firstMs?: number;
}

function OdometerRoll({ value, format, firstMs }: Required<OdometerProps>) {
  const text = format(value);
  const { prefix, core, suffix } = splitNumber(text);
  // Mounts showing the formatted 0, then rolls up to the value.
  const [slots, setSlots] = useState(() => coreSlots(splitNumber(format(0)).core));
  const [first, setFirst] = useState(true);
  const [step, setStep] = useState(0);
  const firstRef = useRef(true);
  const pendingRef = useRef<{ target: OdometerSlot[]; step: number } | null>(null);
  const rootRef = useRef<HTMLSpanElement>(null);

  // Phase 1: lay out the slot row with every reel still at its starting digit.
  useLayoutEffect(() => {
    const target = coreSlots(core);
    const isFirst = firstRef.current;
    firstRef.current = false;
    setFirst(isFirst);
    pendingRef.current = { target, step: step + 1 };
    setSlots((cur) => mergeSlots(cur, target));
    setStep(step + 1);
  }, [core]);

  // Phase 2: once those starting positions are in the DOM, force a style
  // flush so the browser has them as the transition's "from", then point the
  // reels at their targets. Runs before paint, so phase 1 is never seen.
  // Waits for phase 1's own re-render: on mount this effect also runs in the
  // same commit as phase 1, and applying the target there would batch both
  // into one render, so new places would jump instead of rolling up from 0.
  useLayoutEffect(() => {
    const pending = pendingRef.current;
    if (!pending || pending.step !== step) return;
    pendingRef.current = null;
    rootRef.current?.getBoundingClientRect();
    setSlots((cur) => applyTarget(cur, pending.target));
  }, [step]);

  const timing = {
    '--hsab-odo-ms': `${first ? firstMs : Math.min(firstMs, CHANGE_MS)}ms`,
    '--hsab-odo-ease': first ? FIRST_EASE : CHANGE_EASE,
  } as CSSProperties;

  return (
    <>
      <span ref={rootRef} className="hsab-calc__odometer" style={timing} aria-hidden="true">
        {prefix}
        {slots.map((s) => {
          const cls = `hsab-calc__odometer-slot hsab-calc__odometer-slot--${s.status}`;
          return s.digit ? (
            <span key={s.key} className={cls}>
              <span
                className="hsab-calc__odometer-reel"
                style={{ transform: `translateY(${-Number(s.char) * 10}%)` }}
              >
                {REEL.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </span>
            </span>
          ) : (
            <span key={s.key} className={cls}>
              {s.char}
            </span>
          );
        })}
        {suffix}
      </span>
      <span className="hsab-calc__sr-only">{text}</span>
    </>
  );
}
